import { createHttpClient } from '@adapters/HttpClient';
import type { HttpClient } from '@adapters/HttpClient/types';
import { apiClientWH } from '@shared/services/ApiClient';

export interface AmazonFbmShipmentTrackingQuery {
  date_from?: string;
  date_to?: string;
  shop_id?: number;
}

export interface AmazonFbmShipmentTrackingFilters {
  date_from?: string;
  date_to?: string;
  shop_id?: number | null;
}

export interface AmazonFbmShipmentSkippedEntry {
  order_id: number;
  ref: string;
  line_id: number;
  reason: string;
  message: string;
}

/** Filas de ejemplo: claves como columnas del TXT Amazon */
export type AmazonFbmShipmentPreviewRow = Record<string, string>;

export interface AmazonFbmShipmentTrackingPreviewResponse {
  filters: AmazonFbmShipmentTrackingFilters;
  orders_in_query: number;
  total_rows: number;
  skipped: AmazonFbmShipmentSkippedEntry[];
  preview_rows: AmazonFbmShipmentPreviewRow[];
  preview_limit: number;
}

export class AmazonFbmShipmentTrackingApiAdapter {
  private httpClient: HttpClient;

  constructor(httpClient?: HttpClient) {
    this.httpClient = httpClient ?? createHttpClient(apiClientWH);
  }

  private buildParams(q: AmazonFbmShipmentTrackingQuery): Record<string, string | number> {
    const params: Record<string, string | number> = {};
    if (q.date_from) params.date_from = q.date_from;
    if (q.date_to) params.date_to = q.date_to;
    if (q.shop_id != null) params.shop_id = q.shop_id;
    return params;
  }

  /**
   * GET /orders/export/amazon-fbm/shipment-tracking/preview
   */
  async preview(
    q: AmazonFbmShipmentTrackingQuery
  ): Promise<AmazonFbmShipmentTrackingPreviewResponse> {
    return this.httpClient.get<AmazonFbmShipmentTrackingPreviewResponse>(
      '/orders/export/amazon-fbm/shipment-tracking/preview',
      this.buildParams(q),
      {},
      false
    );
  }

  /**
   * GET /orders/export/amazon-fbm/shipment-tracking — descarga TXT (blob)
   */
  async downloadShipmentTracking(q: AmazonFbmShipmentTrackingQuery): Promise<void> {
    const response = await apiClientWH.get<Blob>('/orders/export/amazon-fbm/shipment-tracking', {
      params: this.buildParams(q),
      responseType: 'blob',
    });

    const blob = response.data;
    const disposition = response.headers['content-disposition'] as string | undefined;
    let filename = 'amazon_shipment_tracking.txt';
    if (disposition) {
      const utf8 = disposition.match(/filename\*=UTF-8''([^;\s]+)/i);
      if (utf8?.[1]) {
        filename = decodeURIComponent(utf8[1]);
      } else {
        const simple = disposition.match(/filename="?([^";\n]+)"?/i);
        if (simple?.[1]) filename = simple[1];
      }
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
